// Saved prompts for the agent-queue buttons (Agents → Prompts). public.agent_prompts is append-only: the newest row
// per prompt_id is what the button renders, a null template means "the built-in default" (src/lib/promptTemplates.mjs),
// and the older rows are the history.

import { supabase } from './supabase';
import { PROMPT_DEFAULTS, pageSectionVars, renderPrompt, templateVars, type PromptId, type PromptVars } from './promptTemplates.mjs';

export { PROMPT_DEFAULTS, pageSectionVars, renderPrompt, templateVars, type PromptId, type PromptVars };

export interface PromptVersion {
  id: string;
  prompt_id: string;
  template: string | null;
  note: string;
  created_by: string | null;
  created_at: string;
}

/**
 * The template a button renders right now: the newest saved one, or the default when nobody has saved one.
 * A read error throws, so a button never quietly sends the default when a saved prompt exists.
 */
export async function loadPromptTemplate(id: PromptId): Promise<string> {
  const { data, error } = await supabase
    .from('agent_prompts')
    .select('template')
    .eq('prompt_id', id)
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) throw new Error(`Couldn't read the saved prompt "${id}": ${error.message}`);
  return data?.[0]?.template ?? PROMPT_DEFAULTS[id];
}

/** Every save for one prompt, newest first. */
export async function fetchPromptVersions(id: PromptId): Promise<PromptVersion[]> {
  const { data, error } = await supabase.from('agent_prompts').select('*').eq('prompt_id', id).order('created_at', { ascending: false }).limit(50);
  if (error) throw error;
  return (data ?? []) as PromptVersion[];
}

/** The newest save per prompt, for the list's "edited" marks. */
export async function fetchCurrentPrompts(): Promise<Map<string, PromptVersion>> {
  const { data, error } = await supabase.from('agent_prompts').select('*').order('created_at', { ascending: false }).limit(1000);
  if (error) throw error;
  const map = new Map<string, PromptVersion>();
  for (const row of (data ?? []) as PromptVersion[]) if (!map.has(row.prompt_id)) map.set(row.prompt_id, row);
  return map;
}

/** Save a prompt (null = back to the default). Read back, since an RLS-blocked insert reports no error. */
export async function savePrompt(id: PromptId, template: string | null, createdBy: string | null, note = ''): Promise<PromptVersion> {
  const { data, error } = await supabase
    .from('agent_prompts')
    .insert({ prompt_id: id, template, note: note.trim(), created_by: createdBy })
    .select('*')
    .single();
  if (error || !data) throw new Error(`The prompt wasn't saved: ${error?.message ?? 'no row came back'}`);
  return data as PromptVersion;
}

/** Variables a template uses that this prompt doesn't fill, and ones it fills that the template leaves out. */
export function checkTemplate(template: string, known: string[]): { unknown: string[]; unused: string[] } {
  const used = templateVars(template);
  return { unknown: used.filter((v) => !known.includes(v)), unused: known.filter((v) => !used.includes(v)) };
}
